import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { Sparkles, Clock, BookmarkPlus, BookmarkCheck, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { trustLine } from "@/i18n";
import { useT } from "@/hooks/useT";
import HighlightToClip from "@/components/HighlightToClip";
import HighlightCoachmark from "@/components/HighlightCoachmark";
import DoubtSidebar from "@/components/DoubtSidebar";
import MiniGameModal from "@/components/MiniGameModal";
import WatchLearnPanel from "@/components/WatchLearnPanel";
import FlashcardDeck from "@/components/FlashcardDeck";
import { PARTS, READING_WPM } from "@/components/explain/constants";
import ExplainProgressRail from "@/components/explain/ExplainProgressRail";
import ExplainPartArticle from "@/components/explain/ExplainPartArticle";

/**
 * ExplainDeeperSheet — Iter84 refactor (orchestrator).
 *
 * Owns the lifecycle: load cached parts → check library state → stream
 * generation of missing parts → schedule the mini-game auto-pop.
 *
 * Visual / per-part rendering is extracted to `components/explain/`:
 *   - constants            → PARTS, BCP-47 mapping
 *   - mdComponents         → markdown renderer overrides
 *   - ExplainProgressRail  → 3-pill TOC
 *   - ExplainPartArticle   → one whitepaper part (loading skeleton or
 *                            article with narration, regen, quiz, CTAs)
 */
export default function ExplainDeeperSheet({
  open, onOpenChange, path, stepIndex,
  onCreditsNeeded, onLibraryChange, onNextStep,
}) {
  const { lang } = useT();
  const [parts, setParts] = useState({});
  const [moduleLang, setModuleLang] = useState("en");
  const [loadingPart, setLoadingPart] = useState(null);
  const [regenPart, setRegenPart] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gameOpen, setGameOpen] = useState(false);
  const [gameRewarded, setGameRewarded] = useState(false);
  const [gameAutoPopped, setGameAutoPopped] = useState(false);
  const gameTimerRef = useRef(null);
  const [deckOpen, setDeckOpen] = useState(false);
  const runRef = useRef(0);
  // Refs for each rendered <article> so the progress-rail pills can
  // smooth-scroll to the corresponding section. Indexed by part number.
  const partRefs = useRef({});

  const scrollToPart = (n) => {
    const el = partRefs.current[n];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const step = path?.steps?.[stepIndex];

  // Load cached parts + library state, then stream-generate missing parts.
  useEffect(() => {
    if (!open || !path || stepIndex == null) return;
    const runId = ++runRef.current;
    setParts({});
    setError(null);
    setSaved(false);

    const run = async () => {
      let cached = {};
      try {
        const { data } = await api.get("/counselor/module", {
          params: { path_id: path.path_id, step_index: stepIndex },
        });
        cached = data?.parts || {};
        if (runRef.current !== runId) return;
        if (Object.keys(cached).length) setParts(cached);
        setModuleLang(data?.language || path?.language || "en");
      } catch {
        setModuleLang(path?.language || "en");
      }

      try {
        const { data } = await api.get("/library");
        const exists = (data || []).some(
          (it) => it.path_id === path.path_id && it.step_index === stepIndex,
        );
        if (runRef.current === runId) setSaved(exists);
      } catch {
        /* library read is best-effort */
      }

      for (const { n } of PARTS) {
        if (runRef.current !== runId) return;
        if (cached[String(n)]) continue;
        setLoadingPart(n);
        try {
          const { data } = await api.post("/counselor/explain", {
            path_id: path.path_id,
            step_index: stepIndex,
            step_title: step.title,
            step_detail: step.detail || step.objective || "",
            part: n,
          });
          if (runRef.current !== runId) return;
          setParts((prev) => ({ ...prev, [String(n)]: data }));
        } catch (e) {
          if (runRef.current !== runId) return;
          const status = e?.response?.status;
          if (status === 402) {
            onCreditsNeeded?.();
          } else {
            setError(e?.response?.data?.detail || e.message || "Generation failed");
          }
          break;
        }
      }
      if (runRef.current === runId) setLoadingPart(null);
    };
    run();
  }, [open, path?.path_id, stepIndex]);

  // Schedule the mini-game auto-pop. Triggers ~8s after Part 1 renders
  // (a proxy for "user is actively reading") unless the credit's
  // already been claimed for this step.
  useEffect(() => {
    if (!open || !path?.path_id || stepIndex == null) {
      if (gameTimerRef.current) { clearTimeout(gameTimerRef.current); gameTimerRef.current = null; }
      return;
    }
    setGameAutoPopped(false);
    setGameRewarded(false);
    api.get("/games/step-status", { params: { path_id: path.path_id, step_index: stepIndex } })
      .then(({ data }) => setGameRewarded(!!data?.rewarded))
      .catch(() => {});
  }, [open, path?.path_id, stepIndex]);

  useEffect(() => {
    if (!open || gameAutoPopped || gameRewarded) return;
    if (!parts["1"]) return;
    if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
    gameTimerRef.current = setTimeout(() => {
      setGameOpen(true);
      setGameAutoPopped(true);
    }, 8000);
    return () => { if (gameTimerRef.current) clearTimeout(gameTimerRef.current); };
  }, [open, parts, gameAutoPopped, gameRewarded]);

  const regenerate = async (partN) => {
    // Confirm only when the user will actually be charged. Backend is
    // authoritative on the price; frontend uses created_at as heuristic
    // for the confirm copy.
    const cachedCreatedAt = parts[String(partN)]?.created_at;
    let willCharge = true;
    if (cachedCreatedAt) {
      try {
        const ts = new Date(cachedCreatedAt);
        if (ts < new Date("2026-04-28T00:00:00Z")) willCharge = false;
      } catch {
        /* keep willCharge=true on parse failure */
      }
    }
    if (willCharge) {
      const ok = window.confirm("Regenerating Part " + partN + " will use 1 credit. Continue?");
      if (!ok) return;
    }
    setRegenPart(partN);
    setError(null);
    try {
      const { data } = await api.post("/counselor/regenerate-whitepaper", {
        path_id: path.path_id,
        step_index: stepIndex,
        step_title: step.title,
        step_detail: step.detail || step.objective || "",
        part: partN,
      });
      setParts((prev) => ({ ...prev, [String(partN)]: data }));
      toast.success(
        data.free_regen
          ? "Part " + partN + " regenerated · free (legacy whitepaper)"
          : "Part " + partN + " regenerated · 1 credit used"
      );
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        onCreditsNeeded?.();
      } else {
        setError(e?.response?.data?.detail || e.message || "Regeneration failed");
        toast.error("Regeneration failed");
      }
    } finally {
      setRegenPart(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/library/save", { path_id: path.path_id, step_index: stepIndex });
      setSaved(true);
      toast.success("Saved to My Library");
      onLibraryChange?.();
    } catch (e) {
      if (e?.response?.status !== 402) {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const totalWords = Object.values(parts).reduce((s, p) => s + (p.word_count || 0), 0);
  const readMinutes = Math.max(1, Math.round(totalWords / READING_WPM));
  const completedParts = Object.keys(parts).length;
  const allDone = completedParts >= 3;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#0B0E14]/95 border-white/10 text-white w-full sm:max-w-2xl overflow-y-auto px-5 sm:px-6 md:px-8"
        data-testid="explain-sheet"
      >
        <SheetHeader>
          <div className="label-eyebrow text-[#00FF9D] mb-2 flex items-center gap-2">
            <Sparkles size={12} /> 30-min whitepaper · Oak AI Campus
          </div>
          <SheetTitle className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
            {step?.title || "Deep Dive"}
          </SheetTitle>
          <div className="italic text-[12px] text-white/35 font-display tracking-wide pt-0.5" data-testid="trust-line-explain">
            {trustLine(lang)}
          </div>
          <SheetDescription className="text-white/55 text-xs flex items-center gap-3 pt-1">
            <span className="flex items-center gap-1"><Clock size={11} /> ~{readMinutes} min read</span>
            <span className="font-mono-oak" data-testid="explain-wordcount">{totalWords.toLocaleString()} words</span>
            <span className="text-white/40">· {completedParts}/3 parts</span>
          </SheetDescription>

          <div className="pt-3">
            <button
              onClick={save}
              disabled={!allDone || saving || saved}
              className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${
                saved
                  ? "border-[#00FF9D]/40 bg-[#00FF9D]/10 text-[#9bffcc]"
                  : allDone
                    ? "btn-mint-outline"
                    : "border-white/10 bg-white/5 text-white/40 cursor-not-allowed"
              }`}
              data-testid="btn-save-library"
              title={allDone ? "Save this whitepaper as a PDF in your library" : "Finish generating to save"}
            >
              {saving ? (
                <><Loader2 size={12} className="animate-spin" /> Saving…</>
              ) : saved ? (
                <><BookmarkCheck size={12} /> Saved to My Library</>
              ) : (
                <><BookmarkPlus size={12} /> Save to My Library</>
              )}
            </button>
          </div>
        </SheetHeader>

        <ExplainProgressRail
          parts={parts}
          loadingPart={loadingPart}
          onJump={scrollToPart}
        />

        <HighlightToClip pathId={path?.path_id} className="block mt-7" data-testid="explain-clip-wrap">
          <div className="space-y-10" data-testid="explain-content">
            <HighlightCoachmark />

            {/* Watch & Learn — at the top of the reading flow so a
                short video primes attention before the deep dive. */}
            {step?.title && (
              <WatchLearnPanel
                topic={
                  (step.title || "").trim().length < 8 && path?.goal
                    ? `${path.goal} — ${step.title}`
                    : step.title
                }
                context={path?.goal || ""}
                lang={path?.language || "en"}
              />
            )}

            {error && (
              <div className="glass p-4 border-rose-400/30 text-rose-200 text-sm">
                <div className="font-semibold mb-1">Generation hit a snag</div>
                <div className="text-white/70">{error}</div>
              </div>
            )}

            {PARTS.map((p) => (
              <ExplainPartArticle
                key={p.n}
                part={p}
                data={parts[String(p.n)]}
                loadingPart={loadingPart}
                regenPart={regenPart}
                moduleLang={moduleLang}
                pathId={path?.path_id}
                stepIndex={stepIndex}
                gameRewarded={gameRewarded}
                onRegenerate={regenerate}
                partRefSetter={(el) => { partRefs.current[p.n] = el; }}
                onOpenGame={() => setGameOpen(true)}
                onOpenDeck={() => setDeckOpen(true)}
              />
            ))}
          </div>
        </HighlightToClip>

        {/* Next-Step CTA at the bottom of the reading pane. */}
        {onNextStep && (
          <div
            className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="reading-next-step-row"
          >
            <div className="text-[12px] text-white/55 font-mono-oak">
              {allDone ? (
                <>All 3 parts complete · <span className="text-[#00FF9D]">+ Knowledge Equity</span></>
              ) : (
                <>Continue when you&apos;re ready — your progress is saved.</>
              )}
            </div>
            <button
              onClick={onNextStep}
              disabled={completedParts === 0}
              className="rounded-xl px-5 py-2.5 text-sm font-display inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#0044FF] to-[#5D3FD3] text-white border border-white/10 hover:opacity-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="btn-reading-next-step"
              title={completedParts === 0 ? "Generate the first whitepaper part to continue" : "Move to the next step"}
            >
              Next Step <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Live Doubt Solver — floating button + right-docked sidebar.
            Only renders once a part exists, so backend has context. */}
        {path && stepIndex != null && Object.keys(parts).length > 0 && (
          <DoubtSidebar
            pathId={path.path_id}
            stepIndex={stepIndex}
            stepTitle={step?.title}
            onCreditsNeeded={onCreditsNeeded}
            onCreditsChange={(c) => {
              window.dispatchEvent(new CustomEvent("oak:credits-updated", { detail: { credits: c } }));
            }}
          />
        )}

        {path && stepIndex != null && (
          <MiniGameModal
            open={gameOpen}
            onOpenChange={setGameOpen}
            pathId={path.path_id}
            stepIndex={stepIndex}
            part={1}
            onRewarded={(c) => {
              setGameRewarded(true);
              window.dispatchEvent(new CustomEvent("oak:credits-updated", { detail: { credits: c } }));
            }}
          />
        )}

        {path && stepIndex != null && (
          <FlashcardDeck
            open={deckOpen}
            onOpenChange={setDeckOpen}
            mode="step"
            pathId={path.path_id}
            stepIndex={stepIndex}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
